import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ROLE_PERMISSIONS, type RoleName } from '@es/shared';
import { api, getToken, PERFIL_VISUALIZADO_KEY, setToken, type SessionUser } from './api';
const PERFIS_DISPONIVEIS: readonly RoleName[] = ['ADMINISTRADOR', 'RH', 'GESTOR', 'COLABORADOR'];

function perfilSalvo(): RoleName {
  const role = sessionStorage.getItem(PERFIL_VISUALIZADO_KEY);
  return PERFIS_DISPONIVEIS.includes(role as RoleName) ? (role as RoleName) : 'ADMINISTRADOR';
}

interface AuthState {
  user: SessionUser | null;
  /** Usuário devolvido pela API, sem a simulação visual aplicada. */
  usuarioAutenticado: SessionUser | null;
  loading: boolean;
  entrar: (email: string, password: string) => Promise<void>;
  entrarComToken: (token: string) => Promise<void>;
  sair: () => void;
  pode: (permission: string) => boolean;
  podeEspelharPerfis: boolean;
  perfilVisualizado: RoleName;
  alterarPerfilVisualizado: (role: RoleName) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [usuarioAutenticado, setUsuarioAutenticado] = useState<SessionUser | null>(null);
  const [perfilVisualizado, setPerfilVisualizado] = useState<RoleName>(perfilSalvo);
  const [loading, setLoading] = useState(true);

  // Revalida a sessão no servidor a cada carga: o token guardado pode ter expirado
  // ou o perfil pode ter sido revogado desde o último acesso.
  useEffect(() => {
    let cancelled = false;
    async function restaurar(): Promise<void> {
      if (!getToken()) {
        sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
        setPerfilVisualizado('ADMINISTRADOR');
        setLoading(false);
        return;
      }
      try {
        const me = await api.get<SessionUser>('/me');
        if (!cancelled) {
          setUsuarioAutenticado(me);
          if (!me.roles.includes('ADMINISTRADOR')) {
            sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
            setPerfilVisualizado('ADMINISTRADOR');
          }
        }
      } catch {
        setToken(null);
        sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
        setPerfilVisualizado('ADMINISTRADOR');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void restaurar();
    return () => {
      cancelled = true;
    };
  }, []);

  const entrar = useCallback(async (email: string, password: string) => {
    const result = await api.post<{ accessToken: string; user: SessionUser }>('/auth/login', {
      email,
      password,
    });
    setToken(result.accessToken);
    setUsuarioAutenticado(result.user);
    if (!result.user.roles.includes('ADMINISTRADOR')) {
      sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
      setPerfilVisualizado('ADMINISTRADOR');
    }
  }, []);

  // Usado pelo retorno do login Microsoft: já chegamos com o token, só falta validar e
  // carregar o usuário — reaproveita a mesma rota que a checagem de sessão na carga da página.
  const entrarComToken = useCallback(async (token: string) => {
    setToken(token);
    try {
      const me = await api.get<SessionUser>('/me');
      setUsuarioAutenticado(me);
      if (!me.roles.includes('ADMINISTRADOR')) {
        sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
        setPerfilVisualizado('ADMINISTRADOR');
      }
    } catch (error) {
      setToken(null);
      sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
      setPerfilVisualizado('ADMINISTRADOR');
      throw error;
    }
  }, []);

  const sair = useCallback(() => {
    setToken(null);
    sessionStorage.removeItem(PERFIL_VISUALIZADO_KEY);
    setPerfilVisualizado('ADMINISTRADOR');
    setUsuarioAutenticado(null);
  }, []);

  const podeEspelharPerfis = usuarioAutenticado?.roles.includes('ADMINISTRADOR') ?? false;

  const alterarPerfilVisualizado = useCallback(
    (role: RoleName) => {
      if (!usuarioAutenticado?.roles.includes('ADMINISTRADOR')) return;
      if (!PERFIS_DISPONIVEIS.includes(role)) return;
      sessionStorage.setItem(PERFIL_VISUALIZADO_KEY, role);
      setPerfilVisualizado(role);
    },
    [usuarioAutenticado],
  );

  /**
   * A simulação muda exclusivamente a interface. O token continua sendo o do administrador
   * e toda autorização real segue validada pela API; nunca envie este papel simulado ao servidor.
   */
  const user = useMemo<SessionUser | null>(() => {
    if (!usuarioAutenticado || !podeEspelharPerfis || perfilVisualizado === 'ADMINISTRADOR') {
      return usuarioAutenticado;
    }
    return {
      ...usuarioAutenticado,
      roles: [perfilVisualizado],
      permissions: [...ROLE_PERMISSIONS[perfilVisualizado]],
    };
  }, [perfilVisualizado, podeEspelharPerfis, usuarioAutenticado]);

  const pode = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      usuarioAutenticado,
      loading,
      entrar,
      entrarComToken,
      sair,
      pode,
      podeEspelharPerfis,
      perfilVisualizado,
      alterarPerfilVisualizado,
    }),
    [
      user,
      usuarioAutenticado,
      loading,
      entrar,
      entrarComToken,
      sair,
      pode,
      podeEspelharPerfis,
      perfilVisualizado,
      alterarPerfilVisualizado,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return context;
}
